import { observer } from 'mobx-react-lite';
import { PropsWithChildren, useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useProjectStore } from '../context';
import { KebabCaseString } from '../types';
import { Loader } from './ui';

export const ProjectRoute = observer(({ children }: PropsWithChildren) => {
  /** Context */
  const projectStore = useProjectStore();
  const params = useParams<{ projectName: KebabCaseString }>();
  const location = useLocation();

  /** State **/
  const [destination, setDestination] = useState<string | undefined>(undefined);

  /** Effects */
  // The project list is normally fetched by ProjectSelector, which lives inside the page this
  // route wraps as `children` — but now that we hold off rendering `children` until loading is
  // done (see below), that fetch would never be kicked off on a direct navigation to a project
  // page (a hard refresh, a bookmark, a shared link), deadlocking on the loader forever.
  // fetchProjectList() is deduped against concurrent callers, so calling it here too is safe.
  useEffect(() => {
    projectStore.fetchProjectList();
  }, [projectStore]);

  useEffect(() => {
    if (!projectStore.hasLoadedList) return;

    // Show the project the URL names, not whichever one happened to be current
    if (!params.projectName || !projectStore.openProjectByName(params.projectName)) {
      setDestination('/');
    } else {
      setDestination(undefined);
    }
  }, [projectStore.hasLoadedList, projectStore.projectList, params.projectName]);

  /** Render */
  // Every project-scoped page (Session Estimator, Edits, Hire Talent, ...) is wrapped in this
  // route — rendering `children` before the project list had loaded even once was why each one
  // showed a fully blank page for 1.5s+ on every navigation, with nothing telling the user it was
  // loading. This must key off hasLoadedList, not isLoadingList: isLoadingList legitimately flips
  // back to true on any later background refresh, which would unmount `children` again — and
  // since `children` is what mounts ProjectSelector, which itself calls fetchProjectList() on
  // mount, that unmount/remount became a self-sustaining infinite loop.
  if (!projectStore.hasLoadedList) return <Loader />;
  return <>{destination ? <Navigate to={destination} state={{ from: location }} replace /> : children}</>;
});
